import { useState, useRef, useCallback, type ReactNode } from 'react';
import { LayoutContext } from './LayoutContext';
import { safeLocalStorage } from '@/lib/safeLocalStorage';

const getPersistedSidebarState = (): boolean => {
  const value = safeLocalStorage.getItem('ui.sidebar-collapsed');
  return value ?? false;
};

const saveUserSidebarState = (collapsed: boolean): void => {
  safeLocalStorage.setItem('ui.sidebar-collapsed', collapsed);
};

export function LayoutProvider({ children }: { children: ReactNode }) {
  const [hasSidebar, setHasSidebar] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() =>
    getPersistedSidebarState()
  );
  const sidebarCountRef = useRef(0);

  const registerSidebar = useCallback(() => {
    sidebarCountRef.current += 1;
    setHasSidebar(true);
  }, []);

  const unregisterSidebar = useCallback(() => {
    sidebarCountRef.current -= 1;
    if (sidebarCountRef.current <= 0) {
      sidebarCountRef.current = 0;
      setHasSidebar(false);
    }
  }, []);

  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed(prevState => {
      const newState = !prevState;
      saveUserSidebarState(newState);
      return newState;
    });
  }, []);

  return (
    <LayoutContext.Provider
      value={{
        hasSidebar,
        registerSidebar,
        unregisterSidebar,
        sidebarCollapsed,
        toggleSidebar,
      }}
    >
      {children}
    </LayoutContext.Provider>
  );
}
